import {isEnvConfEnabled} from '../utils/env/isEnvConfEnabled';
import {ENV_CONFIG_VARIABLES} from '../constants/env';

/**
 * ЯНДЕКС МЕТРИКА.
 *
 * Сам счетчик подключается инлайн-снипетом в public/index.html — он создает
 * очередь window.ym и подтягивает библиотеку. Здесь только отправка событий,
 * ровно как у пикселя VK (см. vkPixel.js):
 *
 *  - hit — «просмотр страницы». Мы SPA с одним index.html и без смены URL,
 *    поэтому настоящий просмотр случается ровно один раз за загрузку, а
 *    переходы между экранами Метрика видит только отсюда. Без этого весь
 *    трафик слипался бы в отчетах в одну строку «/», а показатель отказов
 *    считался бы по одному просмотру на визит;
 *  - reachGoal — целевое событие. По целям Яндекс Директ обучает
 *    автостратегии и собирает сегменты ретаргетинга, поэтому цели
 *    расставлены руками: игровые действия происходят на канве Phaser, где
 *    ни кликов по DOM, ни смены URL нет, и автоматически Метрика не найдет
 *    ничего.
 *
 * Имена целей общие с VK — см. adGoals.js.
 */

// Номер счетчика из кабинета Яндекс Метрики (он же в снипете в public/index.html)
export const YANDEX_METRIKA_COUNTER_ID = 112344148;

// Адрес предыдущего виртуального просмотра — реферером следующего.
// Подставлять сюда location.href нельзя: он у нас не меняется никогда, и
// каждый экран сообщал бы реферером самого себя. Метрика по рефереру строит
// отчет переходов, а совпадение реферера с собственным доменом она трактует
// как внутренний переход — источник рекламного визита при этом теряется
let previousHitUrl = null;

// Первый виртуальный просмотр за загрузку страницы отправлять НЕЛЬЗЯ: счетчик
// при инициализации уже отправил хит на адрес входа (см. снипет в
// public/index.html — там ему передан url: location.href). Два хита на одну
// загрузку означали бы, что визита с одним просмотром не бывает никогда, —
// а Метрика именно так определяет отказ. Показатель отказов, на который
// смотрит Директ, стал бы мертвым, а глубина просмотра завышенной на единицу.
//
// Гасим именно НАШ хит, а не автоматический: автоматический несет полный
// адрес входа с yclid/utm (по ним считается источник рекламного визита) и
// уходит даже если приложение упало на загрузке
let isFirstHitSkipped = false;

// Уже отправленный идентификатор игрока. Слать его перед каждым просмотром и
// каждой целью незачем: Метрика привязывает его к визиту, а хук просмотра
// экрана зовут 40+ страниц — это удвоило бы число вызовов счетчика впустую
let sentUserId = null;

const sendUserId = (userId) => {
  if (!userId || String(userId) === sentUserId) {
    return;
  }
  sentUserId = String(userId);
  window.ym(YANDEX_METRIKA_COUNTER_ID, 'setUserID', sentUserId);
};

const checkIfEnabled = () => {
  if (isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_DISABLE_YANDEX_METRIKA)) {
    return false;
  }
  // Локальная разработка не должна попадать в статистику кампаний
  if (process.env.NODE_ENV !== 'production') {
    return false;
  }
  return typeof window !== 'undefined' && typeof window.ym === 'function';
};

/**
 * Виртуальный просмотр «страницы»: у нас это экран приложения.
 * @param {string} pageId - идентификатор экрана (см. usePageAnalytics)
 * @param {string} [userId] - id игрока для склейки визитов между устройствами
 */
export const trackYandexPageView = (pageId, userId) => {
  if (!checkIfEnabled()) return;

  try {
    // Отдельным вызовом и ДО просмотра: Метрика привязывает идентификатор к
    // визиту, а не к конкретному хиту
    sendUserId(userId);
    // Экраны URL не меняют — путь собираем сами, иначе в отчете все просмотры
    // сливаются в одну строку «/»
    const hitUrl = `${window.location.origin}/${pageId}`;

    if (!isFirstHitSkipped) {
      isFirstHitSkipped = true;
      // Реферером следующего экрана становится адрес входа: именно его
      // сообщил автоматический хит, который мы сейчас не стали дублировать
      previousHitUrl = window.location.href;
      return;
    }

    window.ym(YANDEX_METRIKA_COUNTER_ID, 'hit', hitUrl, {
      title: pageId,
      // На первом просмотре реферер не трогаем вовсе: его Метрика уже знает из
      // document.referrer при инициализации, и это как раз рекламный источник
      ...(previousHitUrl ? {referer: previousHitUrl} : {}),
    });
    previousHitUrl = hitUrl;
  } catch (error) {
    console.error('[YandexMetrika] hit failed:', error);
  }
};

/**
 * Целевое событие.
 * @param {string} goal - имя цели из AD_GOALS (должно совпадать с кабинетом)
 * @param {Object} [options]
 * @param {string} [options.userId] - id игрока
 * @param {number} [options.value] - ценность события (в отчетах — «доход» цели)
 * @param {Object} [options.params] - произвольные параметры события
 */
export const trackYandexGoal = (goal, {userId, value, params} = {}) => {
  if (!checkIfEnabled() || !goal) return;

  try {
    sendUserId(userId);
    // Метрика принимает параметры цели одним объектом; ценность лежит в нем же
    // под зарезервированным ключом order_price
    const goalParams = {
      ...(params || {}),
      ...(value !== undefined ? {order_price: value} : {}),
    };
    const hasParams = Object.keys(goalParams).length > 0;

    if (hasParams) {
      window.ym(YANDEX_METRIKA_COUNTER_ID, 'reachGoal', goal, goalParams);
    } else {
      window.ym(YANDEX_METRIKA_COUNTER_ID, 'reachGoal', goal);
    }
  } catch (error) {
    console.error('[YandexMetrika] reachGoal failed:', error);
  }
};
